import { Link } from "react-router-dom";
import { Check, X, ArrowRight, FileText, HelpCircle } from "lucide-react";
import Seo, { ORIGIN_URL } from "../components/Seo";
import CarpetCardDownload from "../components/CarpetCardDownload";
import {
  CARPET_INTRO, CARPET_KEY_PRINCIPLE, CARPET_FACTORS,
  CARPET_RETAIN, CARPET_REPLACE, CARPET_INSTALL_ASK, CARPET_CONCLUSION,
} from "../data/carpetCard";

const PATH = "/resources/carpet-keep-vs-replace";
const TITLE = "Carpet: Keep vs. Replace, A Condition-Based Decision Guide";
const DESC =
  "Should you keep, clean, or replace your carpet? A calm, engineering-based framework for evaluating synthetic and other carpet by condition, moisture, odor, staining, cleanability, and padding, not by material type. Free one-page printable card.";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: TITLE,
    description: DESC,
    author: { "@type": "Organization", name: "My Eco Engineer" },
    publisher: { "@type": "Organization", name: "My Eco Engineer" },
    mainEntityOfPage: `${ORIGIN_URL}${PATH}`,
    about: ["carpet", "indoor air quality", "residential environmental design", "low-tox home"],
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to decide whether to keep or replace carpet",
    description:
      "Evaluate existing carpet by condition rather than material type to decide whether to keep and clean it, or replace it.",
    step: [
      { "@type": "HowToStep", name: "Assess condition", text: "Check moisture history, odor, dust, staining, cleanability, and padding condition." },
      { "@type": "HowToStep", name: "Keep or clean", text: CARPET_RETAIN.join(" ") },
      { "@type": "HowToStep", name: "Replace", text: CARPET_REPLACE.join(" ") },
      { "@type": "HowToStep", name: "Specify new carpet", text: CARPET_INSTALL_ASK.join(" ") },
    ],
  },
];

function List({ items, tone }) {
  const Icon = tone === "keep" ? Check : X;
  const color = tone === "keep" ? "text-primary" : "text-foreground/70";
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 text-sm text-foreground/80 leading-relaxed">
          <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${color}`} strokeWidth={2} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

export default function CarpetCardPage() {
  return (
    <div data-testid="carpet-card-page" className="home-white">
      <Seo title={TITLE} description={DESC} path={PATH} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="border-b border-border bg-[#0a241a] text-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16 sm:py-20">
          <Link to="/greenpapers" data-testid="carpet-back" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary hover:gap-3 transition-all">
            <ArrowRight className="h-3.5 w-3.5 rotate-180" /> Resources
          </Link>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">Printable Worksheet · Interior Living Reference</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-medium leading-[1.05] tracking-tight">
            Carpet: Keep vs. Replace
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed">{CARPET_INTRO}</p>
          <div className="mt-8">
            <CarpetCardDownload
              testid="carpet-page-download-hero"
              label="Get the free one-page card"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 font-mono text-[11px] uppercase tracking-widest font-medium hover:bg-primary-hover transition-colors"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16 space-y-14">
        {/* KEY PRINCIPLE */}
        <section data-testid="carpet-principle" className="relative border border-primary/30 bg-primary/5 p-6 sm:p-8">
          <p className="mono-label text-primary">Key Principle</p>
          <p className="mt-3 text-base text-foreground/85 leading-relaxed">{CARPET_KEY_PRINCIPLE}</p>
        </section>

        {/* FACTORS */}
        <section>
          <h2 className="font-display text-2xl font-medium tracking-tight">Evaluate carpet against these factors</h2>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {CARPET_FACTORS.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border border-border text-foreground/70 bg-surface">
                {f}
              </span>
            ))}
          </div>
        </section>

        {/* KEEP vs REPLACE */}
        <section className="grid md:grid-cols-2 gap-8">
          <div data-testid="carpet-keep" className="border border-primary/30 bg-base p-6 sm:p-7">
            <h2 className="font-display text-xl font-medium text-primary">Keep or clean the existing carpet when</h2>
            <List items={CARPET_RETAIN} tone="keep" />
          </div>
          <div data-testid="carpet-replace" className="border border-border bg-surface p-6 sm:p-7">
            <h2 className="font-display text-xl font-medium">Replace the existing carpet when</h2>
            <List items={CARPET_REPLACE} tone="replace" />
          </div>
        </section>

        {/* ASK BEFORE INSTALL */}
        <section>
          <h2 className="font-display text-2xl font-medium tracking-tight flex items-center gap-2.5">
            <HelpCircle className="h-6 w-6 text-primary" strokeWidth={1.5} /> Before installing new carpet, ask
          </h2>
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {CARPET_INSTALL_ASK.map((q, i) => (
              <li key={i} className="flex gap-3 text-sm text-foreground/80 leading-relaxed">
                <span className="text-primary font-semibold">&rarr;</span><span>{q}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CONCLUSION */}
        <section className="border-l-2 border-primary pl-5">
          <p className="mono-label text-primary">In Practice</p>
          <p className="mt-2 text-base text-foreground/85 leading-relaxed">{CARPET_CONCLUSION}</p>
        </section>

        {/* DOWNLOAD CTA */}
        <section data-testid="carpet-cta" className="relative border border-border bg-surface p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-primary/40 text-primary bg-base">
            <FileText className="h-7 w-7" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-xl font-medium leading-snug">Download the one-page printable card</h2>
            <p className="mt-2 text-sm text-foreground/65 leading-relaxed max-w-2xl">
              Join the email list and we'll send the print-and-tick PDF right away, plus new Greenpapers as they publish. Unsubscribe anytime.
            </p>
          </div>
          <div className="shrink-0">
            <CarpetCardDownload
              testid="carpet-page-download-cta"
              label="Get the Carpet Card"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 font-mono text-[11px] uppercase tracking-widest font-medium hover:bg-primary-hover transition-colors"
            />
          </div>
        </section>

        {/* related */}
        <section className="border-t border-border pt-8">
          <p className="text-sm text-foreground/60">
            This card is drawn from the Rugs and Carpeting reference in{" "}
            <Link to="/products/low-tox-home-blueprint" className="text-primary hover:underline">The Low-Tox Home Blueprint</Link>, the full room-by-room residential environmental design manual.
          </p>
        </section>
      </div>
    </div>
  );
}
